/**
 * 블랙리스트 차단 로그 페이지
 *
 * 차단된 기기의 접근 시도 이력을 최신순으로 표시한다.
 * 차단 사유별 색상 배지와 요청 유형 태그를 제공하며, 기기 주소로 검색 가능하다.
 */
import { useQuery } from "@tanstack/react-query";
import { blacklistLogService } from "../../services/blacklistLogService";
import { BlacklistAttemptLog } from "../../types/blacklistLog";
import { useState, useMemo } from "react";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorAlert from "../../components/ui/ErrorAlert";
import { formatDate } from "../../utils/format";

const REASON_STYLE: Record<string, { label: string; cls: string }> = {
  "사전차단_MAC": {
    label: "사전차단 (MAC)",
    cls: "bg-rose-100 text-rose-700",
  },
  "사전차단_번호": {
    label: "사전차단 (번호)",
    cls: "bg-fuchsia-100 text-fuchsia-700",
  },
  "차단된_MAC_등록시도": {
    label: "MAC 등록 시도",
    cls: "bg-orange-100 text-orange-700",
  },
  "기기_블랙리스트": {
    label: "기기 블랙리스트",
    cls: "bg-red-100 text-red-700",
  },
  "번호_블랙리스트": {
    label: "번호 블랙리스트",
    cls: "bg-pink-100 text-pink-700",
  },
  "일별체크_차단": {
    label: "일별 체크",
    cls: "bg-purple-100 text-purple-700",
  },
  // ── 추적 전용(사유 G - 기타) ────────────────────────────────────────────
  // 차단하지 않고 사용 기록만 남긴 건. 실제 차단 건과 구분해서 보여준다.
  "추적_MAC": {
    label: "추적 중 (MAC)",
    cls: "bg-slate-100 text-slate-700",
  },
  "추적_번호": {
    label: "추적 중 (번호)",
    cls: "bg-slate-100 text-slate-700",
  },
  "일별체크_추적": {
    label: "추적 중 (일별 체크)",
    cls: "bg-slate-100 text-slate-700",
  },
};

const REQUEST_TYPE_STYLE: Record<string, { label: string; cls: string }> = {
  REGISTRATION: {
    label: "등록 요청",
    cls: "bg-blue-100 text-blue-700",
  },
  DAILY_CHECK: {
    label: "일별 체크",
    cls: "bg-gray-100 text-gray-600",
  },
};

const ITEMS_PER_PAGE = 50;

export default function BlacklistLogList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { data: logs, isLoading, error } = useQuery({
    queryKey: ["blacklist-logs"],
    queryFn: blacklistLogService.getLogs,
  });

  const filtered = useMemo(() => {
    if (!logs) return [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return logs;
    return logs.filter(
      (l) =>
        l.deviceAddress.toLowerCase().includes(term) ||
        (l.userId ?? "").toLowerCase().includes(term) ||
        (l.phoneNumber ?? "").includes(term)
    );
  }, [logs, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paginated = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleSearchChange = (v: string) => {
    setSearchTerm(v);
    setCurrentPage(1);
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message="차단 로그를 불러오지 못했습니다." />;

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">차단 로그</h1>
          <p className="text-gray-500 mt-1">
            블랙리스트 기기의 접근 시도 이력 ({filtered.length}건)
          </p>
        </div>
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-3">
          <i className="fas fa-shield-alt text-red-500 text-xl"></i>
          <div>
            <p className="text-xs text-red-400 font-medium">전체 차단</p>
            <p className="text-2xl font-bold text-red-600">{logs?.length ?? 0}</p>
          </div>
        </div>
      </div>

      {/* 검색 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-150 p-5">
        <div className="relative max-w-md">
          <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
          <input
            type="text"
            placeholder="기기 주소 / 유저 ID / 전화번호 검색"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>

      {/* 테이블 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-150 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-150">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">#</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">기기 주소 (MAC)</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">유저 ID</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">전화번호</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">차단 사유</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">요청 유형</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">발생 시각</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-gray-400">
                    <i className="fas fa-shield-alt text-4xl mb-3 block opacity-30"></i>
                    {searchTerm ? "검색 결과가 없습니다." : "차단 기록이 없습니다."}
                  </td>
                </tr>
              ) : (
                paginated.map((log: BlacklistAttemptLog) => {
                  const reason = REASON_STYLE[log.blockedReason] ?? {
                    label: log.blockedReason,
                    cls: "bg-gray-100 text-gray-600",
                  };
                  const reqType = REQUEST_TYPE_STYLE[log.requestType] ?? {
                    label: log.requestType,
                    cls: "bg-gray-100 text-gray-600",
                  };
                  return (
                    <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-400 font-mono text-xs">{log.id}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">
                        {log.deviceAddress}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs max-w-[160px] truncate">
                        {log.userId ?? <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                        {log.phoneNumber || <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${reason.cls}`}>
                          <i className="fas fa-ban text-[10px]"></i>
                          {reason.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${reqType.cls}`}>
                          {reqType.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {formatDate(log.createdAt)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
              {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)} / {filtered.length}건
            </p>
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                이전
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                const page = start + i;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      page === currentPage
                        ? "bg-primary text-white border-primary"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
